import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Lock, Sparkles, Crown, Zap } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { usePlan } from "@/store/PlanContext";

// Tag discreta de cadeado "Disponível no Plano Max"
export const LockedTag = ({ className = "" }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border border-amber-200/70 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ${className}`}>
    <Lock className="h-3 w-3" />
    Disponível no Plano Max
  </span>
);

// Card de paywall elegante para seções bloqueadas
export const PlanPaywall = ({ title, desc, testId = "plan-paywall" }) => {
  const { setPlan } = usePlan();
  const upgrade = () => {
    setPlan("max");
    toast.success("Plano Max ativado!", { description: "Todos os recursos avançados foram desbloqueados." });
  };
  return (
    <div data-testid={testId} className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 p-8 text-center shadow-[0_8px_30px_rgba(15,23,42,0.15)]">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 backdrop-blur">
        <Crown className="h-6 w-6 text-amber-300" />
      </div>
      <h3 className="font-heading text-xl font-bold text-white">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-300">{desc}</p>
      <button
        data-testid="paywall-upgrade-btn"
        onClick={upgrade}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-lg transition-colors hover:bg-blue-500"
      >
        <Zap className="h-4 w-4" />
        Fazer Upgrade por R$ 899/mês
      </button>
    </div>
  );
};

// Modal amigável de upgrade (ex: limite de médicos)
export const UpgradeModal = ({ open, onOpenChange, title, message }) => {
  const { setPlan } = usePlan();
  const confirm = () => {
    setPlan("max");
    toast.success("Plano Max ativado!", { description: "Cadastro ilimitado de médicos liberado." });
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="upgrade-modal" className="max-w-md">
        <DialogHeader>
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-50">
            <Crown className="h-5 w-5 text-amber-500" />
          </div>
          <DialogTitle className="font-heading text-lg font-bold text-slate-900">{title}</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-slate-500">{message}</DialogDescription>
        </DialogHeader>
        <div className="mt-2 flex gap-2">
          <button onClick={() => onOpenChange(false)} className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50">Agora não</button>
          <button data-testid="upgrade-confirm-btn" onClick={confirm} className="flex-1 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blue-700">Upgrade p/ Max</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Seletor rápido de planos (ferramenta de teste), flutuante em todas as telas internas
export const PlanSwitcher = () => {
  const { plan, setPlan } = usePlan();
  const location = useLocation();
  const shown = ["/agenda", "/financeiro", "/medicos", "/diagnostico", "/recepcao", "/atendimento"];
  if (!shown.includes(location.pathname)) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="plan-switcher"
        className="fixed bottom-4 left-4 z-50 flex items-center gap-1.5 rounded-2xl border border-slate-200/70 bg-white/90 p-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-md"
      >
        <span className="flex items-center gap-1 px-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">
          <Sparkles className="h-3 w-3" /> Plano
        </span>
        <button
          data-testid="plan-switch-basic"
          onClick={() => setPlan("basic")}
          className={`rounded-xl px-2.5 py-1.5 text-xs font-bold transition-colors sm:px-3 ${plan === "basic" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
        >
          Basic<span className="hidden sm:inline"> · R$399</span>
        </button>
        <button
          data-testid="plan-switch-max"
          onClick={() => setPlan("max")}
          className={`rounded-xl px-2.5 py-1.5 text-xs font-bold transition-colors sm:px-3 ${plan === "max" ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}
        >
          Max<span className="hidden sm:inline"> · R$899</span>
        </button>
      </motion.div>
    </AnimatePresence>
  );
};
